<template>
  <v-card class="ma-1" :loading="isLoading">
    <v-card-item v-if="name">
      <v-card-title class="text-subtitle-1">
        {{ name }}
      </v-card-title>
    </v-card-item>

    <v-card-text>
      <div style="position: absolute; bottom: 0; left: 0; width: 23px; height: 230px;" class="mb-4 ms-2" v-if="!hideButtons">
        <v-btn variant="outlined" color="grey" icon="mdi-chevron-left" rounded="0"
          class="h-100 rounded" v-on:click="prev"></v-btn>
      </div>

      <div class="w-100" style="height: 230px;">
        <canvas v-bind:id="'chart-' + id"></canvas>
      </div>

      <div style="position: absolute; bottom: 0; right: 0; width: 23px; height: 230px;" class="mb-4 me-2" v-if="!hideButtons">
        <v-btn variant="outlined" color="grey" icon="mdi-chevron-right" rounded="0"
          class="h-100 rounded" v-on:click="next"></v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from 'vue';

import { Chart } from 'chart.js/auto';
import 'chartjs-adapter-date-fns';

import annotationPlugin from 'chartjs-plugin-annotation';
Chart.register(annotationPlugin);

import axios from 'axios';

import config from '@/config.js';

export default defineComponent({
  name: 'ChartArea',

  props: {
    id: { type: String },

    hideButtons: { type: Boolean, default: false },

    startDatetime: { type: String },
    endDatetime: { type: String },

    timeSpan: { type: Number },

    forceUpdate: { type: String },

    name: { type: String },

    yAxis: { type: Object },

    channels: {
      type: Array,

      default(/* rawProps */) {
        return [];
      }
    },

    referenceLines: {
      type: Array,

      default(/* rawProps */) {
        return [];
      }
    }
  },

  emits: ['prev', 'next'],

  setup(props, { emit }) {

    const prev = () => {
      emit('prev');
    };

    const next = () => {
      emit('next');
    };


    const BorderColors = ['#03A9F4', '#FFC107', '#4CAF50', '#673AB7'];

    const isLoading = ref(false);

    var chart;

    onMounted(() => {

      chart = new Chart(document.getElementById('chart-' + props.id), {
        type: 'line',

        data: {
          datasets: []
        },

        options: {
          scales: {
            x: {
              type: 'time',
              time: {
                unit: 'minute'
              },
              ticks: {
                stepSize: 30,
                callback: axisLabelCallbackHalf
              },

              min: '1970-01-01 00:00:00',
              max: '1970-01-01 24:00:00'
            },

            y: {
              min: props.yAxis.left.min,
              max: props.yAxis.left.max
            },

            y2: {
              min: props.yAxis.right.min,
              max: props.yAxis.right.max,

              position: 'right',

              grid: {
                drawOnChartArea: !props.yAxis.right.isSameLeft
              },

              border: {
                dash: [5, 2]
              }
            }
          },

          parsing: {
            xAxisKey: 'time',
            yAxisKey: 'value'
          },

          elements: {
            line: {
              borderWidth: 1
            },

            point: {
              radius: 0
            }
          },

          spanGaps: [true, true, true, true],

          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                font: {
                  size: 10
                }
              }
            },

            tooltip: {
              callbacks: {
                title: (context) => {
                  return context[0].dataset.data[context[0].dataIndex].time;
                }
              }
            },

            annotation: {
              annotations: {}
            }
          },

          layout: {
            padding: {
              left: 20,
              right: 20
            }
          },

          maintainAspectRatio: false
        }
      });

      props.channels.forEach((channel, index) => {

        chart.data.datasets.push({
          borderColor: BorderColors[index],
          backgroundColor: BorderColors[index],

          label: channel.moduleID + '#' + channel.ch,

          elements: {
            line: {
              stepped: channel.ch.startsWith('d')
            }
          },

          yAxisID: (channel.scaleYAxis === 'left') ? 'y' : 'y2'
        });

      });

      props.referenceLines.forEach((refLine) => {

        chart.options.plugins.annotation.annotations[refLine.key] = {
          type: 'line',
          yMin: refLine.position,
          yMax: refLine.position,
          borderColor: '#E53935', /* red-darken-1 */
          borderWidth: 1,

          arrowHeads: {
            start: {
              display: (refLine.scaleYAxis === 'left'),
              fill: true,
              length: 4,
              width: 2
            },
            end: {
              display: (refLine.scaleYAxis === 'right'),
              fill: true,
              length: 4,
              width: 2
            }
          },

          yScaleID: (refLine.scaleYAxis === 'left') ? 'y' : 'y2'
        };

      });

      // showChart();

      watch(props, () => {

        props.channels.forEach((channel, index) => {

          if (channel.label) {
            chart.data.datasets[index].label = channel.label;
          }

          if (channel.intervalMinutes) {
            chart.options.spanGaps[index] = channel.intervalMinutes * 60 * 1000
          }

        });

        showChart();
      });

    });

    const showChart = () => {

      isLoading.value = true;

      setTimeAxisLabel();

      const promises = [];

      props.channels.forEach((channel) => {
        promises.push(
          axios.get(
            config.API_URL +
            `/modules/${ channel.moduleID }` +
            `/channels/${ channel.ch }` +
            `/measurements?start=${ props.startDatetime }&end=${ props.endDatetime }`,
            { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem("access-token") } }
          )
        );
      });

      Promise.all(
        promises
      ).then((values) => {

        values.forEach((value, index) => {
          chart.data.datasets[index].data = value.data.measurements;
        });

        chart.options.scales.x.min = props.startDatetime;
        chart.options.scales.x.max = props.endDatetime;

        chart.update();

        isLoading.value = false;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };

    const setTimeAxisLabel = () => {
      if (props.timeSpan == 1) {

        chart.options.scales.x.ticks.stepSize = 5;
        chart.options.scales.x.ticks.callback = axisLabelCallbackEvery10Minutes;

      } else if (props.timeSpan == 3) {

        chart.options.scales.x.ticks.stepSize = 10;
        chart.options.scales.x.ticks.callback = axisLabelCallbackEvery10Minutes;

      } else {

        chart.options.scales.x.ticks.stepSize = 30;
        chart.options.scales.x.ticks.callback = axisLabelCallbackHalf;

      }
    };

    const axisLabelCallbackEvery10Minutes = (val/* , index */) => {
      const date = new Date(val);
      const minutes = date.getMinutes();

      if (minutes % 10) {
        return ''
      } else {
        return date.getHours().toString().padStart(2, '0') + ':' + minutes.toString().padStart(2, '0');
      }
    };

    const axisLabelCallbackHalf = (val, index) => {
      const date = new Date(val);

      if (index % 2) {
        return '';
      } else {
        return date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
      }
    };

    return {
      prev,
      next,

      isLoading
    }

  }
});
</script>

